/**
 * BudgetClientInfo Component
 *
 * Componente para exibir e editar informações do cliente e condições comerciais
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Building,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Edit2,
  Save,
  X,
  ExternalLink,
  CreditCard,
  Calendar,
  DollarSign,
  Info,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

interface Client {
  id: string;
  name: string;
  document?: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  contactName?: string;
  industry?: string;
  notes?: string;
}

interface BudgetClientInfoProps {
  client: Client;
  responsible?: string;
  paymentTerms?: string;
  deliveryTerms?: string;
  validityDays?: number;
  notes?: string;
  onClientUpdate?: (client: Partial<Client>) => void;
  onConditionsUpdate?: (conditions: {
    responsible?: string;
    paymentTerms?: string;
    deliveryTerms?: string;
    validityDays?: number;
    notes?: string;
  }) => void;
  editable?: boolean;
  showClientLink?: boolean;
  className?: string;
}

const PAYMENT_TERMS_OPTIONS = [
  { value: 'vista', label: 'À Vista' },
  { value: '7dias', label: '7 dias' },
  { value: '15dias', label: '15 dias' },
  { value: '30dias', label: '30 dias' },
  { value: '45dias', label: '45 dias' },
  { value: '60dias', label: '60 dias' },
  { value: 'parcelado', label: 'Parcelado' },
  { value: 'personalizado', label: 'Personalizado' }
];

const DELIVERY_TERMS_OPTIONS = [
  { value: 'imediato', label: 'Imediato' },
  { value: '24h', label: '24 horas' },
  { value: '48h', label: '48 horas' },
  { value: '7dias', label: '7 dias' },
  { value: '15dias', label: '15 dias' },
  { value: '30dias', label: '30 dias' },
  { value: 'a_combinar', label: 'A combinar' },
  { value: 'personalizado', label: 'Personalizado' }
];

export function BudgetClientInfo({
  client,
  responsible,
  paymentTerms,
  deliveryTerms,
  validityDays = 30,
  notes,
  onClientUpdate,
  onConditionsUpdate,
  editable = true,
  showClientLink = true,
  className
}: BudgetClientInfoProps) {
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [isEditingConditions, setIsEditingConditions] = useState(false);
  const [editedClient, setEditedClient] = useState(client);
  const [editedConditions, setEditedConditions] = useState({
    responsible,
    paymentTerms,
    deliveryTerms,
    validityDays,
    notes
  });

  const handleSaveClient = () => {
    onClientUpdate?.(editedClient);
    setIsEditingClient(false);
  };

  const handleSaveConditions = () => {
    onConditionsUpdate?.(editedConditions);
    setIsEditingConditions(false);
  };

  const handleCancelClient = () => {
    setEditedClient(client);
    setIsEditingClient(false);
  };

  const handleCancelConditions = () => {
    setEditedConditions({
      responsible,
      paymentTerms,
      deliveryTerms,
      validityDays,
      notes
    });
    setIsEditingConditions(false);
  };

  const isIndividualClient = !client.document || client.document.length === 11; // CPF
  const documentType = isIndividualClient ? 'CPF' : 'CNPJ';

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Client Information Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Informações do Cliente
            </CardTitle>
            {editable && (
              <div className="flex gap-2">
                {isEditingClient ? (
                  <>
                    <Button size="sm" onClick={handleSaveClient}>
                      <Save className="h-4 w-4 mr-1" />
                      Salvar
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancelClient}>
                      <X className="h-4 w-4 mr-1" />
                      Cancelar
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setIsEditingClient(true)}>
                    <Edit2 className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {isEditingClient ? (
            // Edit Mode
            <div className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nome do Cliente *</Label>
                  <Input
                    value={editedClient.name}
                    onChange={(e) => setEditedClient({ ...editedClient, name: e.target.value })}
                    placeholder="Nome completo ou razão social"
                  />
                </div>
                <div>
                  <Label>{documentType}</Label>
                  <Input
                    value={editedClient.document || ''}
                    onChange={(e) => setEditedClient({ ...editedClient, document: e.target.value })}
                    placeholder={isIndividualClient ? '000.000.000-00' : '00.000.000/0000-00'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>E-mail *</Label>
                  <Input
                    type="email"
                    value={editedClient.email}
                    onChange={(e) => setEditedClient({ ...editedClient, email: e.target.value })}
                    placeholder="cliente@exemplo.com"
                  />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={editedClient.phone || ''}
                    onChange={(e) => setEditedClient({ ...editedClient, phone: e.target.value })}
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>

              <div>
                <Label>Endereço Completo</Label>
                <Input
                  value={editedClient.address || ''}
                  onChange={(e) => setEditedClient({ ...editedClient, address: e.target.value })}
                  placeholder="Rua, número, complemento"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>CEP</Label>
                  <Input
                    value={editedClient.zipCode || ''}
                    onChange={(e) => setEditedClient({ ...editedClient, zipCode: e.target.value })}
                    placeholder="00000-000"
                  />
                </div>
                <div>
                  <Label>Cidade</Label>
                  <Input
                    value={editedClient.city || ''}
                    onChange={(e) => setEditedClient({ ...editedClient, city: e.target.value })}
                    placeholder="São Paulo"
                  />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Input
                    value={editedClient.state || ''}
                    onChange={(e) => setEditedClient({ ...editedClient, state: e.target.value })}
                    placeholder="SP"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Contato Principal</Label>
                  <Input
                    value={editedClient.contactName || ''}
                    onChange={(e) => setEditedClient({ ...editedClient, contactName: e.target.value })}
                    placeholder="Nome do contato"
                  />
                </div>
                <div>
                  <Label>Ramo de Atividade</Label>
                  <Input
                    value={editedClient.industry || ''}
                    onChange={(e) => setEditedClient({ ...editedClient, industry: e.target.value })}
                    placeholder="Tecnologia, Varejo, etc."
                  />
                </div>
              </div>

              {client.notes && (
                <div>
                  <Label>Observações do Cliente</Label>
                  <Textarea
                    value={editedClient.notes || ''}
                    onChange={(e) => setEditedClient({ ...editedClient, notes: e.target.value })}
                    placeholder="Informações adicionais sobre o cliente..."
                    rows={3}
                  />
                </div>
              )}
            </div>
          ) : (
            // View Mode
            <div className="space-y-4">
              {/* Client Header */}
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-lg">{client.name}</h3>
                    {client.industry && (
                      <Badge variant="secondary">{client.industry}</Badge>
                    )}
                  </div>
                  {client.document && (
                    <p className="text-muted-foreground">
                      {documentType}: {client.document}
                    </p>
                  )}
                </div>

                {showClientLink && (
                  <Button variant="ghost" size="sm">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Ver Ficha Completa
                  </Button>
                )}
              </div>

              {/* Contact Information */}
              <div className="grid gap-3 text-sm">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{client.email}</span>
                  <Badge variant="outline" className="text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verificado
                  </Badge>
                </div>

                {client.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{client.phone}</span>
                    <Button variant="ghost" size="sm" className="h-auto p-1">
                      <Phone className="h-3 w-3" />
                    </Button>
                  </div>
                )}

                {client.contactName && (
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>Contato: {client.contactName}</span>
                  </div>
                )}

                {client.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <span>{client.address}</span>
                      {client.city && client.state && (
                        <span className="block text-muted-foreground">
                          {client.city}, {client.state} {client.zipCode && `- ${client.zipCode}`}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {client.notes && (
                <>
                  <Separator />
                  <div className="bg-muted/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Observações do Cliente</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{client.notes}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commercial Conditions Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Condições Comerciais
            </CardTitle>
            {editable && (
              <div className="flex gap-2">
                {isEditingConditions ? (
                  <>
                    <Button size="sm" onClick={handleSaveConditions}>
                      <Save className="h-4 w-4 mr-1" />
                      Salvar
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancelConditions}>
                      <X className="h-4 w-4 mr-1" />
                      Cancelar
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setIsEditingConditions(true)}>
                    <Edit2 className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isEditingConditions ? (
            // Edit Mode
            <div className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Vendedor Responsável</Label>
                  <Input
                    value={editedConditions.responsible || ''}
                    onChange={(e) => setEditedConditions({ ...editedConditions, responsible: e.target.value })}
                    placeholder="Nome do vendedor"
                  />
                </div>
                <div>
                  <Label>Validade (dias)</Label>
                  <Input
                    type="number"
                    value={editedConditions.validityDays || 30}
                    onChange={(e) => setEditedConditions({ ...editedConditions, validityDays: parseInt(e.target.value) || 30 })}
                    min="1"
                    max="365"
                  />
                </div>
              </div>

              <div>
                <Label>Condições de Pagamento</Label>
                <Select
                  value={editedConditions.paymentTerms || '30dias'}
                  onValueChange={(value) => setEditedConditions({ ...editedConditions, paymentTerms: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione as condições" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Prazo de Entrega</Label>
                <Select
                  value={editedConditions.deliveryTerms || 'a_combinar'}
                  onValueChange={(value) => setEditedConditions({ ...editedConditions, deliveryTerms: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o prazo" />
                  </SelectTrigger>
                  <SelectContent>
                    {DELIVERY_TERMS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Observações do Orçamento</Label>
                <Textarea
                  value={editedConditions.notes || ''}
                  onChange={(e) => setEditedConditions({ ...editedConditions, notes: e.target.value })}
                  placeholder="Adicione informações importantes sobre este orçamento..."
                  rows={3}
                />
              </div>
            </div>
          ) : (
            // View Mode
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-muted-foreground">Vendedor Responsável:</span>
                    <p className="font-medium">{responsible || 'Não definido'}</p>
                  </div>

                  <div>
                    <span className="text-sm text-muted-foreground">Validade da Proposta:</span>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{validityDays} dias</p>
                      <Badge variant="outline" className="text-xs">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(Date.now() + validityDays * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR')}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-muted-foreground">Condições de Pagamento:</span>
                    <p className="font-medium flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      {paymentTerms || 'Não definido'}
                    </p>
                  </div>

                  <div>
                    <span className="text-sm text-muted-foreground">Prazo de Entrega:</span>
                    <p className="font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {deliveryTerms || 'A combinar'}
                    </p>
                  </div>
                </div>
              </div>

              {notes && (
                <>
                  <Separator />
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Observações do Orçamento</h4>
                        <p className="text-sm text-blue-800 dark:text-blue-200 whitespace-pre-wrap">{notes}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {validityDays < 7 && (
                <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-orange-800 dark:text-orange-200">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      Validade curta: Este orçamento expira em breve ({validityDays} dias)
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}